<script setup>
// The Schedule Builder wizard — opened imperatively via
// useModal().open(ScheduleBuilderModal). Purely a view over useScheduleBuilder()
// (Task 11): `state` drives which of the four Figma views renders, the
// composable owns all persistence/SSE plumbing.
const open = defineModel('open', { default: true });
const emit = defineEmits(['close']);

const {
    prefs,
    state,
    progress,
    limitedCapacityCount,
    loadPrefs,
    suggestPrefs,
    savePrefs,
    build,
} = useScheduleBuilder();

import { useScheduleAnalytics } from '@/composables/useScheduleAnalytics'
const { track } = useScheduleAnalytics()

// Local editable draft — kept separate from the shared `prefs` state so typing
// in the field doesn't leak into other consumers until Save/Build is pressed.
const draftPrefs = ref('');
const submitting = ref(false);
const errorMsg = ref('');

// A returning viewer is one with saved preferences (Asana 666646) — the variant
// exists to pre-populate them, so it can't apply to someone with none.
//
// Deliberately NOT the builder's interaction counter: the launcher records the
// interaction before this modal mounts, so the count is already ≥1 on a
// first-ever open, and it's per-device localStorage besides. loadPrefs() reads
// the user's own saved text only — never the Bizzabo profile seed — so a
// first-timer whose field gets AI-prefilled below still reads as new.
const returning = ref(false);

onMounted(async () => {
    track('schedule assistant opened', { feature: 'schedule-builder', mode: 'builder' });
    try {
        await loadPrefs();
        draftPrefs.value = prefs.value || '';
        returning.value = !!draftPrefs.value.trim();
        // Only reach for an AI pre-fill when the user has no saved prefs yet —
        // suggestPrefs() may legitimately return '' (no source data), which is fine.
        if (!draftPrefs.value) {
            try {
                const suggested = await suggestPrefs();
                // Re-check AFTER the await. suggestPrefs() is an LLM call that
                // takes ~1-2s, and the field is live the whole time — the
                // emptiness check above is stale by the time this resolves. If
                // the user started typing in that window, applying the pre-fill
                // would wipe what they wrote, so only fill a field that's STILL
                // empty. An empty suggestion is a no-op for the same reason:
                // assigning '' would clear their text rather than leave it.
                const suggestedText = suggested?.prefs || '';
                if (suggestedText && !draftPrefs.value) draftPrefs.value = suggestedText;
            } catch { /* best-effort pre-fill only — field just starts empty */ }
        }
    } catch { /* field ships editable/empty if prefs can't be loaded */ }
});

const submitBuild = async () => {
    // Required (Asana 687576): the builder can't tailor a schedule with no input,
    // so block the build and surface an error rather than running on empty prefs.
    if (!draftPrefs.value.trim()) {
        // Blunt on purpose (Asana 687576): the softer "Please add some
        // information..." read as a suggestion and was missed in QA entirely.
        errorMsg.value = 'This field is required';
        return;
    }
    submitting.value = true;
    errorMsg.value = '';
    try {
        await savePrefs(draftPrefs.value);
        build();
    } catch {
        errorMsg.value = 'Something went wrong saving your preferences. Please try again.';
    } finally {
        submitting.value = false;
    }
};

// Clear the required-field error once the user starts typing.
watch(draftPrefs, () => { if (errorMsg.value && draftPrefs.value.trim()) errorMsg.value = ''; });

const close = () => { open.value = false; };

const headline = computed(() => {
    switch (state.value) {
        case 'working':
            return 'Working on building your schedule. It might take a few minutes!';
        case 'complete':
            return limitedCapacityCount.value > 0
                ? 'Your schedule is ready, but you’ll still need to save seats later for limited capacity sessions.'
                : 'Your schedule is ready';
        case 'full':
            return 'Looks like your schedule is already full!';
        default:
            // "Scheduler Builder" was a typo — the product is the Schedule Builder,
            // as the returning headline in Asana 666646 confirms.
            return returning.value
                ? 'Welcome back to our Agenda Builder!'
                : 'Welcome to our Agenda Builder!';
    }
});

// Not dismissable while the build is running — closing mid-stream would leave
// the user unsure whether the run is still generating their schedule.
const dismissable = computed(() => state.value !== 'working');

import { modal_button_build_2026, modal_button_outline_2026, modal_close_link_2026 } from '~~/shared/utilities/ui';

// The build CTA carries an inline sparkle, so it needs flex layout + a disabled
// state on top of the shared Build treatment (darker-pink → magenta, matching
// every other Build My Schedule entry point — Asana 687571).
const ctaBuildCls = `inline-flex items-center justify-center gap-[8px] disabled:opacity-60 ${modal_button_build_2026}`;
const closeLinkCls = modal_close_link_2026;
const ctaOutlineCls = modal_button_outline_2026;
</script>

<template>
    <FragmentsModalShell
        v-model:open="open"
        :headline="headline"
        :dismissable="dismissable"
        @close="emit('close')"
    >
        <!-- PREFERENCES -->
        <FragmentsDialogsSchedulePrefs
            v-if="state === 'prefs'"
            v-model="draftPrefs"
            :submitting="submitting"
            :error="errorMsg"
            :returning="returning"
        />

        <!-- WORKING. The warning box hugs its text (whitespace-nowrap, shrink-to-fit)
             per Figma — the old w-full inside max-w-[500px] over-constrained it and
             forced the copy to stack. The shell supplies the 110px side padding.
             Mobile keeps it full-width and lets the text wrap. -->
        <div v-else-if="state === 'working'" class="flex flex-col items-center w-full">
            <FragmentsLoading :loading="true" />

            <p v-if="progress" class="mt-[20px] font-hubspot-sans font-light text-[15px] leading-[1.5] text-(--magenta)">
                {{ progress }}
            </p>

            <div class="flex items-center gap-[14px] w-full sm:w-auto text-left bg-white rounded-[10px] px-[20px] py-[10px] mt-[28px]">
                <svg class="shrink-0 mt-[2.5px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="font-hubspot-sans font-light text-[14px] leading-[1.5] text-(--magenta) m-0 sm:whitespace-nowrap">
                    Please do not close this window or this process might be cancelled.
                </p>
            </div>
        </div>

        <!-- COMPLETE. No inner max-width so the content shares the shell's 110px side
             padding with the headline (they looked mismatched otherwise). -->
        <div v-else-if="state === 'complete'" class="flex flex-col items-center w-full text-center">
            <div v-if="limitedCapacityCount > 0" class="flex items-start gap-[12px] w-full text-left bg-white rounded-[10px] p-[20px]">
                <svg class="shrink-0 mt-[3.5px] size-[16px] text-(--magenta)" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path d="M8 4V9" stroke="white" stroke-width="1.5" stroke-linecap="round" />
                    <circle cx="8" cy="11.6" r="0.9" fill="white" />
                </svg>
                <p class="font-hubspot-sans font-light text-[15px] leading-[23px] text-(--magenta) m-0">
                    Limited capacity sessions require a saved seat in order to attend. Seat saving opens
                    for VIP pass holders on August 25 at 11:00 AM ET and on September 1 at 11:00 AM ET for
                    General Admission pass holders.
                </p>
            </div>

            <p class="font-hubspot-sans font-light text-[15px] leading-[23px] text-(--magenta) mt-[20px] m-0">
                If any sessions on your schedule don't feel right, simply remove them and our schedule
                builder can make some new suggestions to fill that time slot. You can also edit your
                schedule preferences at any time.
            </p>
        </div>

        <!-- SCHEDULE FULL -->
        <div v-else-if="state === 'full'" class="flex flex-col items-center w-full text-center">
            <p class="font-hubspot-sans font-light text-[15px] leading-[23px] text-(--magenta) m-0">
                We didn't find any open time left across your schedule to fill — every day is already
                booked solid. If you'd like new suggestions, try removing a session or two, or edit your
                schedule preferences and build again.
            </p>
        </div>

        <template #actions>
            <!-- Column so the Close link stacks below the primary CTA (Figma). -->
            <div class="flex flex-col items-center gap-[20px]">
                <template v-if="state === 'prefs'">
                    <button type="button" :disabled="submitting" :class="ctaBuildCls" @click="submitBuild">
                        <svg viewBox="0 0 13 13" fill="none" class="size-[13px] shrink-0" aria-hidden="true">
                            <path d="M13 8.09719C13 8.21502 12.9044 8.31057 12.7875 8.31057C10.9214 8.31057 8.71296 10.5186 8.71296 12.3843C8.71296 12.5021 8.61738 12.5977 8.50046 12.5977C8.38354 12.5977 8.28797 12.5021 8.28797 12.3843C8.28704 10.5186 6.07856 8.31057 4.2125 8.31057C4.09465 8.31057 4 8.21502 4 8.09719C4 7.97937 4.09558 7.88381 4.2125 7.88381C6.07856 7.88381 8.28704 5.67579 8.28704 3.81104C8.28704 3.69321 8.38262 3.59766 8.49954 3.59766C8.61645 3.59766 8.71203 3.69321 8.71203 3.81104C8.71203 5.67579 10.9205 7.88381 12.7866 7.88381C12.9044 7.88381 12.9991 7.97937 12.9991 8.09719H13Z" fill="currentColor" />
                            <path d="M6 3.59735C6 3.6759 5.93628 3.7396 5.85834 3.7396C4.61429 3.7396 3.14197 5.21161 3.14197 6.4554C3.14197 6.53395 3.07826 6.59766 3.00031 6.59766C2.92236 6.59766 2.85865 6.53395 2.85865 6.4554C2.85803 5.21161 1.38571 3.7396 0.141664 3.7396C0.0630993 3.7396 0 3.6759 0 3.59735C0 3.5188 0.0637179 3.45509 0.141664 3.45509C1.38571 3.45509 2.85803 1.98308 2.85803 0.73991C2.85803 0.661361 2.92174 0.597656 2.99969 0.597656C3.07764 0.597656 3.14135 0.661361 3.14135 0.73991C3.14135 1.98308 4.61367 3.45509 5.85772 3.45509C5.93628 3.45509 5.99938 3.5188 5.99938 3.59735H6Z" fill="currentColor" />
                        </svg>
                        Build My Schedule
                    </button>
                    <button type="button" :class="closeLinkCls" @click="close">Close</button>
                </template>

                <button v-else-if="state === 'complete' || state === 'full'" type="button" :class="ctaOutlineCls" @click="close">
                    View My Schedule
                </button>
            </div>
        </template>
    </FragmentsModalShell>
</template>
